import { memo, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { useAppContext } from "../../../../context/appContext";
import { useTheme } from "@mui/material/styles";
import KsbDialogActions from "../../../../components/KsbDialogActions";
import BoxHeader from "../../../../../3_apps/5_Husibuech/components/1_Boxes/BoxHeader";
import userService from "../../../../service/user.service";

function PasswordUpdate() {
  const theme = useTheme();
  const { currentUser } = useAppContext();

  const [change, setChange] = useState(false);
  const [loading, setLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordRep, setNewPasswordRep] = useState("");

  const [errors, setErrors] = useState({
    current: "",
    new: "",
    repeat: "",
  });

  const [message, setMessage] = useState("");
  const [saveColor, setSaveColor] = useState("primary");

  // ✅ reset helper
  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setNewPasswordRep("");
    setErrors({ current: "", new: "", repeat: "" });
    setChange(false);
  };

  // ✅ validation
  const validate = () => {
    let valid = true;
    let nextErrors = { current: "", new: "", repeat: "" };

    if (!currentPassword) {
      nextErrors.current = "Aktuelles Passwort fehlt";
      valid = false;
    }

    if (!newPassword) {
      nextErrors.new = "Neues Passwort eingeben";
      valid = false;
    } else if (newPassword.length < 6) {
      nextErrors.new = "Mindestens 6 Zeichen";
      valid = false;
    } else if (newPassword === currentPassword) {
      nextErrors.new = "Neues Passwort darf nicht gleich sein";
      valid = false;
    }

    if (newPassword !== newPasswordRep) {
      nextErrors.repeat = "Passwörter stimmen nicht überein";
      valid = false;
    }

    setErrors(nextErrors);
    return valid;
  };

  // ✅ submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      setMessage("Bitte Eingaben prüfen");
      setTimeout(() => {
        setMessage("");
      }, 3000);
      return;
    }

    try {
      setLoading(true);

      await userService.updateUser(
        "password",
        {
          currentPassword,
          newPassword,
        },
        currentUser.id,
      );

      setSaveColor("success");
      setMessage("Passwort erfolgreich geändert");
      resetForm();
    } catch (err) {
      setSaveColor("warning");
      setMessage(err.response?.data?.message || "Ungültige Angaben");
    } finally {
      setLoading(false);

      setTimeout(() => {
        setSaveColor("primary");
        setMessage("");
      }, 3000);
    }
  };

  const sectionStyle = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 2,
    p: 3,
    boxShadow: 2,
  };

  return (
    <Box sx={sectionStyle}>
      <BoxHeader title="Passwort ändern" />

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        {/* Current */}
        <TextField
          fullWidth
          label="Aktuelles Passwort"
          type="password"
          value={currentPassword}
          onChange={(e) => {
            setCurrentPassword(e.target.value);
            setErrors((prev) => ({ ...prev, current: "" }));
          }}
          error={!!errors.current}
          helperText={errors.current}
        />

        {/* New */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            mt: 2,
          }}
        >
          <TextField
            fullWidth
            label="Neues Passwort"
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setChange(true);
              setErrors((prev) => ({ ...prev, new: "" }));
            }}
            error={!!errors.new}
            helperText={errors.new}
          />

          <TextField
            fullWidth
            label="Wiederholen"
            type="password"
            value={newPasswordRep}
            onChange={(e) => {
              setNewPasswordRep(e.target.value);
              setErrors((prev) => ({ ...prev, repeat: "" }));
            }}
            error={!!errors.repeat}
            helperText={errors.repeat}
          />
        </Box>

        {/* Actions */}
        {change && (
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <KsbDialogActions
              onCancel={(e) => {
                e.preventDefault();
                resetForm();
              }}
              onConfirm={handleSubmit}
              confirmLabel="Speichern"
              confirmDisabled={loading}
            />
          </Box>
        )}

        {/* Message */}
        {message && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor:
                saveColor === "success"
                  ? theme.palette.success.light
                  : theme.palette.warning.light,
            }}
          >
            <Typography variant="body2">{message}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default memo(PasswordUpdate);
